/** Types + pure helpers for the Normal User Analytics tab. No I/O here, so the
 *  formatting and the "has the admin changed the filters?" rule are unit
 *  testable without touching the network. */

export type UsagePeriod = 'today' | 'yesterday' | 'last7' | 'last30' | 'custom';

export interface NormalUserUsageSummary {
  totalRegistered: number;
  currentlyOnline: number;
  currentlyActiveInApp: number;
  activeToday: number;
  newRegistrationsToday: number;
  totalUsageSeconds: number;
  averageUsageSeconds: number;
  activeUsersInPeriod: number;
  newRegistrationsInPeriod: number;
}

export interface NormalUserUsageRow {
  userId: string;
  handle: string;
  displayName: string | null;
  phoneLast4: string | null;
  online: boolean;
  activeInApp: boolean;
  lastActivityAt: string | null;
  usageSeconds: number;
  sessions: number;
  registeredAt: string;
}

export interface NormalUserUsageDaily {
  day: string;
  activeUsers: number;
  usageSeconds: number;
  newRegistrations: number;
}

export interface NormalUserUsageReport {
  summary: NormalUserUsageSummary;
  daily: NormalUserUsageDaily[];
  rows: NormalUserUsageRow[];
  total: number;
  page: number;
  pageSize: number;
  generatedAt: string;
  trackingSince: string | null;
  from: string;
  to: string;
  activeWindowSeconds: number;
}

/** Everything that changes WHAT the server returns. Staged in the UI and sent
 *  only when the admin presses the button — never on a keystroke or a render. */
export interface UsageFilters {
  period: UsagePeriod;
  from?: string;
  to?: string;
  q: string;
  page: number;
  pageSize: number;
}

export const DEFAULT_FILTERS: UsageFilters = {
  period: 'today',
  q: '',
  page: 0,
  pageSize: 25,
};

export const PERIOD_OPTIONS: { value: UsagePeriod; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last7', label: 'Last 7 days' },
  { value: 'last30', label: 'Last 30 days' },
  { value: 'custom', label: 'Custom range' },
];

/** True when the staged filters differ from the ones the shown data came from,
 *  i.e. the figures on screen no longer answer the question being asked. */
export function filtersDiffer(a: UsageFilters, b: UsageFilters | null): boolean {
  if (!b) return false;
  return (
    a.period !== b.period ||
    (a.from ?? '') !== (b.from ?? '') ||
    (a.to ?? '') !== (b.to ?? '') ||
    a.q.trim() !== b.q.trim() ||
    a.page !== b.page ||
    a.pageSize !== b.pageSize
  );
}

/** A custom range needs both ends before it can be asked for. */
export function filtersReady(f: UsageFilters): boolean {
  return f.period !== 'custom' || (!!f.from && !!f.to);
}

/** Query string for the one endpoint this tab calls. */
export function toQuery(f: UsageFilters): Record<string, string | number> {
  const q: Record<string, string | number> = {
    period: f.period,
    page: f.page,
    pageSize: f.pageSize,
  };
  if (f.q.trim()) q.q = f.q.trim();
  if (f.period === 'custom' && f.from && f.to) {
    q.from = f.from;
    q.to = f.to;
  }
  return q;
}

/** "2h 14m", "6m 02s", "—". Durations are measured foreground time. */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '—';
  const s = Math.round(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, '0')}s`;
  return `${sec}s`;
}

/** "14 Oct 2026" — a registration date, where the time of day is noise. */
export function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

/** "14 Oct" — a chart axis tick, where the year is noise. */
export function formatDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
}

/** Local time, to the second — this is a "when did I last ask?" stamp. */
export function formatStamp(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}
